import { Stack, Typography } from "@mui/material";
import { EMPTY_CHARACTER } from "@pulse/shared-components";
import {
  Dialog,
  DropdownInputField,
  DropdownItem,
  FoundationColorTokens,
  Spacing,
  Typography as LeoTypography,
} from "@surya-digital/leo-reactjs-material-ui";
import { TFunction } from "i18next";
import { observer } from "mobx-react";
import { Instance } from "mobx-state-tree";
import React, { useEffect, useState } from "react";
import { Trans } from "react-i18next";
import { DialogErrorContent } from "../../../../components/DialogErrorContent";
import {
  TranslationsStore,
  UpdateLanguageConfigurationError,
} from "../../store/TranslationsStore";

interface ChangeLanguageOrientationDialogProps {
  open: boolean;
  languageCode: string;
  surveyId: string;
  projectId: string;
  languageId: string;
  isRTL: boolean;
  translationsStore: Instance<typeof TranslationsStore>;
  t: TFunction;
  spacing: Spacing;
  typography: LeoTypography;
  tokens: FoundationColorTokens<string>;
  onClose: VoidFunction;
  onSuccess: () => Promise<void>;
}

type LanguageOrientation = "LTR" | "RTL";

export const ChangeLanguageOrientationDialog = observer(
  ({
    open,
    surveyId,
    projectId,
    languageId,
    isRTL,
    translationsStore,
    t,
    spacing,
    typography,
    tokens,
    onClose,
    onSuccess,
  }: ChangeLanguageOrientationDialogProps): React.ReactElement => {
    const currentOrientation: LanguageOrientation = isRTL ? "RTL" : "LTR";
    const [selectedOrientation, setSelectedOrientation] =
      useState<LanguageOrientation>(currentOrientation);
    const hasError =
      translationsStore.updateLanguageConfigurationError !== null;

    useEffect(() => {
      if (open) {
        setSelectedOrientation(currentOrientation);
        translationsStore.resetUpdateLanguageConfigurationState();
      }
    }, [open, currentOrientation]);

    const getErrorText = (): string => {
      switch (translationsStore.updateLanguageConfigurationError) {
        case UpdateLanguageConfigurationError.ProjectAlreadyArchived:
          return t("surveys.translations.languageAction.projectArchivedError");
        case UpdateLanguageConfigurationError.TranslationUpdateInProgress:
          return t(
            "surveys.translations.languageAction.translationUpdateInProgressError",
          );
        default:
          return t("surveys.translations.languageAction.unexpectedError");
      }
    };

    const closeDialog = (): void => {
      translationsStore.resetUpdateLanguageConfigurationState();
      onClose();
    };

    return (
      <Dialog
        open={open}
        title={t("surveys.translations.orientationDialog.title")}
        primaryButtonText={
          hasError
            ? undefined
            : t("surveys.translations.languageSettings.changeOrientationAction")
        }
        onPrimaryButtonClick={async () => {
          if (translationsStore.isUpdatingLanguageConfiguration) {
            return;
          }
          await translationsStore.updateLanguageConfiguration(
            surveyId,
            projectId,
            languageId,
            selectedOrientation === "RTL",
          );
          if (translationsStore.updateLanguageConfigurationError === null) {
            await onSuccess();
            closeDialog();
          }
        }}
        isPrimaryButtonDisabled={selectedOrientation === currentOrientation}
        secondaryButtonText={hasError ? t("common.close") : t("common.cancel")}
        onSecondaryButtonClick={closeDialog}
        isSecondaryButtonDisabled={
          translationsStore.isUpdatingLanguageConfiguration
        }
        disableBackdropClick={true}
        contentPadding={spacing.spaceLG}
        width="560px"
      >
        {hasError ? (
          <DialogErrorContent
            spacing={spacing}
            tokens={tokens}
            typography={typography}
            errorText={getErrorText()}
          />
        ) : (
          <Stack gap={spacing.spaceLG} width="100%">
            <Stack gap={spacing.spaceXXS}>
              <Typography {...typography.s2} color={tokens.label}>
                {t("surveys.translations.languageConfiguration.label")}
              </Typography>
              <Typography {...typography.b2} color={tokens.labelSubtle}>
                {t("surveys.translations.orientationDialog.description")}
              </Typography>
              <Typography {...typography.b2} color={tokens.labelSubtle}>
                <Trans
                  i18nKey="surveys.translations.orientationDialog.directionDescription"
                  components={{
                    span: <span style={{ ...typography.s2 }} />,
                  }}
                />
              </Typography>
            </Stack>
            <DropdownInputField
              name="languageOrientation"
              label={EMPTY_CHARACTER}
              value={selectedOrientation}
              options={[
                {
                  value: "LTR",
                  name: t(
                    "surveys.translations.languageConfiguration.leftToRight",
                  ),
                },
                {
                  value: "RTL",
                  name: t(
                    "surveys.translations.languageConfiguration.rightToLeft",
                  ),
                },
              ]}
              onSelect={(item: DropdownItem): void => {
                setSelectedOrientation(item.value as LanguageOrientation);
              }}
              isDisabled={translationsStore.isUpdatingLanguageConfiguration}
              width="100%"
            />
          </Stack>
        )}
      </Dialog>
    );
  },
);
